import { useState } from 'react';
import { importBarcodes } from '../api/manifests-api';

const MIN_BARCODE_LENGTH = 2;

interface BarcodeImportSectionProps {
  manifestId: number;
  onImported: () => void;
}

export function BarcodeImportSection({ manifestId, onImported }: BarcodeImportSectionProps) {
  const [barcodes, setBarcodes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [warning, setWarning] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setWarning('');
    setIsLoading(true);

    try {
      // Pre-filter barcodes that are too short
      const allLines = barcodes
        .split(/[\n\r,\t]+/)
        .map((b) => b.trim())
        .filter((b) => b.length > 0);

      const tooShort = allLines.filter((b) => b.length < MIN_BARCODE_LENGTH);
      const validLines = allLines.filter((b) => b.length >= MIN_BARCODE_LENGTH);

      if (validLines.length === 0) {
        setError(
          tooShort.length > 0
            ? `All barcodes were too short (minimum ${MIN_BARCODE_LENGTH} characters).`
            : 'Please enter at least one barcode.'
        );
        setIsLoading(false);
        return;
      }

      // Rejoin valid barcodes for the API call
      const filteredInput = validLines.join('\n');

      const response = await importBarcodes(manifestId, { barcodes: filteredInput });
      if (response.success && response.data) {
        const { added, duplicatesSkipped } = response.data;
        setSuccess(`Added ${added} barcodes. ${duplicatesSkipped} duplicates skipped.`);
        if (tooShort.length > 0) {
          setWarning(
            `${tooShort.length} barcode(s) were skipped because they are shorter than ${MIN_BARCODE_LENGTH} characters.`
          );
        }
        setBarcodes('');
        onImported();
      } else {
        setError(response.message || 'Failed to import barcodes');
      }
    } catch {
      setError('An error occurred while importing barcodes');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
      <h3 className="text-lg font-semibold mb-4">Import Barcodes</h3>

      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      {warning && (
        <div className="bg-yellow-500/10 border border-yellow-500 text-yellow-500 px-4 py-3 rounded mb-4">
          {warning}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="barcodes" className="block text-sm font-medium mb-2">
            Paste barcodes (one per line)
          </label>
          <textarea
            id="barcodes"
            value={barcodes}
            onChange={(e) => setBarcodes(e.target.value)}
            rows={6}
            className="w-full px-4 py-2 rounded bg-gray-700 border border-gray-600 focus:border-blue-500 focus:outline-none resize-y"
            placeholder="Enter barcodes here (one per line)"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading || !barcodes.trim()}
          className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 disabled:cursor-not-allowed rounded transition-colors"
        >
          {isLoading ? 'Importing...' : 'Import Barcodes'}
        </button>
      </form>
    </div>
  );
}
